import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/common/Modal';
import { Employee, Department, EmployeeStatus } from '../../types';
import { CreateEmployeeDto, UpdateEmployeeDto, employeesService } from '../../services/api/employees';
import { departmentsService } from '../../services/api/departments';
import { useToast } from '../../contexts/ToastContext';

interface EmployeeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  employee?: Employee | null;
}

const EmployeeFormModal: React.FC<EmployeeFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  employee,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const isEditMode = !!employee;

  const [departments, setDepartments] = useState<Department[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const [formData, setFormData] = useState({
    employeeCode: '',
    fullName: '',
    email: '',
    departmentId: '',
    managerId: '',
    joinDate: '',
    status: 'ACTIVE',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      try {
        const [depts, emps] = await Promise.all([
          departmentsService.getAll(),
          employeesService.getAll({ pageSize: 1000 }),
        ]);
        setDepartments(depts);
        setEmployees(emps.items);
      } catch (err) {
        showError('Failed to load data');
      } finally {
        setLoadingData(false);
      }
    };

    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (employee) {
      setFormData({
        employeeCode: employee.employeeCode,
        fullName: employee.fullName,
        email: employee.email,
        departmentId: String(employee.departmentId),
        managerId: employee.managerId ? String(employee.managerId) : '',
        joinDate: employee.joinDate.split('T')[0],
        status: employee.status,
      });
    } else {
      setFormData({
        employeeCode: '',
        fullName: '',
        email: '',
        departmentId: '',
        managerId: '',
        joinDate: new Date().toISOString().split('T')[0],
        status: 'ACTIVE',
      });
    }
    setErrors({});
  }, [employee, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.employeeCode.trim()) {
      newErrors.employeeCode = t('errors.validation') + ': Employee code is required';
    }
    if (!formData.fullName.trim()) {
      newErrors.fullName = t('errors.validation') + ': Full name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = t('errors.validation') + ': Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('errors.validation') + ': Invalid email format';
    }
    if (!formData.departmentId) {
      newErrors.departmentId = t('errors.validation') + ': Department is required';
    }
    if (!formData.joinDate) {
      newErrors.joinDate = t('errors.validation') + ': Join date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      if (isEditMode && employee) {
        const updateData: UpdateEmployeeDto = {
          employeeCode: formData.employeeCode,
          fullName: formData.fullName,
          email: formData.email,
          departmentId: Number(formData.departmentId),
          managerId: formData.managerId ? Number(formData.managerId) : null,
          joinDate: formData.joinDate,
          status: formData.status as EmployeeStatus,
        };
        await employeesService.update(employee.id, updateData);
        showSuccess(t('employees.edit') + ' - ' + t('common.success'));
      } else {
        const createData: CreateEmployeeDto = {
          employeeCode: formData.employeeCode,
          fullName: formData.fullName,
          email: formData.email,
          departmentId: Number(formData.departmentId),
          managerId: formData.managerId ? Number(formData.managerId) : undefined,
          joinDate: formData.joinDate,
          status: formData.status as EmployeeStatus,
        };
        await employeesService.create(createData);
        showSuccess(t('employees.create') + ' - ' + t('common.success'));
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Operation failed';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Filter out current employee from manager list
  const availableManagers = employees.filter((emp) => emp.id !== employee?.id);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? t('employees.edit') : t('employees.create')}
      size="medium"
    >
      {loadingData ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>{t('common.loading')}</div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>{t('employees.employeeCode')} *</label>
              <input
                type="text"
                name="employeeCode"
                value={formData.employeeCode}
                onChange={handleChange}
                className={errors.employeeCode ? 'error' : ''}
                placeholder="e.g., EMP001"
              />
              {errors.employeeCode && <div className="form-error">{errors.employeeCode}</div>}
            </div>
            <div className="form-group">
              <label>{t('employees.fullName')} *</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className={errors.fullName ? 'error' : ''}
                placeholder="Full name"
              />
              {errors.fullName && <div className="form-error">{errors.fullName}</div>}
            </div>
          </div>

          <div className="form-group">
            <label>{t('employees.email')} *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              placeholder="employee@company.com"
            />
            {errors.email && <div className="form-error">{errors.email}</div>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('employees.department')} *</label>
              <select
                name="departmentId"
                value={formData.departmentId}
                onChange={handleChange}
                className={errors.departmentId ? 'error' : ''}
              >
                <option value="">-- Select Department --</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name} ({dept.code})
                  </option>
                ))}
              </select>
              {errors.departmentId && <div className="form-error">{errors.departmentId}</div>}
            </div>
            <div className="form-group">
              <label>{t('employees.manager')}</label>
              <select name="managerId" value={formData.managerId} onChange={handleChange}>
                <option value="">-- No Manager --</option>
                {availableManagers.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.fullName} ({emp.employeeCode})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('employees.joinDate')} *</label>
              <input
                type="date"
                name="joinDate"
                value={formData.joinDate}
                onChange={handleChange}
                className={errors.joinDate ? 'error' : ''}
              />
              {errors.joinDate && <div className="form-error">{errors.joinDate}</div>}
            </div>
            <div className="form-group">
              <label>{t('employees.status')}</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option value="ACTIVE">{t('status.active')}</option>
                <option value="INACTIVE">{t('status.inactive')}</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="button button-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="button button-primary" disabled={loading}>
              {loading ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default EmployeeFormModal;
